/**
 * UserActionSection Component
 *
 * Single status/comment per execution (shared across all flows)
 */

import React, { useState, useEffect, useCallback } from "react";
import Select from "react-select";
import styles from "./FlowReport.module.css";
import { makeCygnusApiCall } from "../../api/api";
import { CustomButton } from "../../reusable-components/CustomButtonComponent/button";

interface StatusOption {
  label: string;
  value: string;
}

interface UserActionSectionProps {
  cygnusExecutionId: string;
  currentStatus?: string | null;
  currentComment?: string | null;
  updatedBy?: string | null;
  updatedDate?: string | null;
  onSaveSuccess?: (status: string, comment: string) => void;
}

const UserActionSection: React.FC<UserActionSectionProps> = ({
  cygnusExecutionId,
  currentStatus,
  currentComment,
  updatedBy,
  updatedDate,
  onSaveSuccess,
}) => {
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([]);
  const [status, setStatus] = useState<StatusOption | null>(null);
  const [comment, setComment] = useState<string>(currentComment || "");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Fetch status options on mount
  useEffect(() => {
    const fetchStatuses = async () => {
      setIsLoading(true);
      try {
        const response = await makeCygnusApiCall({
          type: "GET",
          payload: {},
          url: "/flow/user-action-statuses/",
          show_message: false,
          customized_success_message: "Got your User Action data.",
          customized_failure_message:
            "Unable to fetch the User Action data, please try again.",
          customized_error_message:
            "Error occurred while fetching User Action data.",
          isblob: false,
        });

        const statuses = response?.data?.statuses;
        if (statuses && Array.isArray(statuses)) {
          setStatusOptions(statuses);
        }
      } catch (error) {
        console.error("Failed to fetch status options:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchStatuses();
  }, []);

  // Sync with props when they change
  useEffect(() => {
    if (currentStatus && statusOptions.length > 0) {
      const found = statusOptions.find((opt) => opt.value === currentStatus);
      setStatus(found || null);
    } else {
      setStatus(null);
    }
    setComment(currentComment || "");
    setHasChanges(false);
  }, [currentStatus, currentComment, cygnusExecutionId, statusOptions]);

  // Track changes
  useEffect(() => {
    const statusChanged = (status?.value || "") !== (currentStatus || "");
    const commentChanged = comment !== (currentComment || "");
    setHasChanges(statusChanged || commentChanged);
  }, [status, comment, currentStatus, currentComment]);

  const handleSave = useCallback(async () => {
    if (!cygnusExecutionId) return;

    setIsSaving(true);
    setSaveMessage(null);

    try {
      const response = await makeCygnusApiCall({
        type: "PUT",
        payload: {
          cygnusExecutionId,
          userActionStatus: status?.value || null,
          userComment: comment || null,
        },
        url: "/flow/execution/user-action/",
        show_message: true,
        customized_success_message: "Updated successfully.",
        customized_failure_message:
          "Unable to update the user action data, please try again.",
        customized_error_message:
          "Error occurred while updating user action data.",
        isblob: false,
      });

      if (response?.success || response?.status) {
        setSaveMessage({ type: "success", text: "Saved" });
        setHasChanges(false);
        onSaveSuccess?.(status?.value || "", comment);

        setTimeout(() => setSaveMessage(null), 3000);
      } else {
        setSaveMessage({
          type: "error",
          text: response?.message || "Failed to save",
        });
      }
    } catch (error) {
      console.error("Save error:", error);
      setSaveMessage({ type: "error", text: "Failed to save" });
    } finally {
      setIsSaving(false);
    }
  }, [cygnusExecutionId, status, comment, onSaveSuccess]);

  const formatDate = (dateValue?: string | null): string => {
    if (!dateValue) return "";
    try {
      return new Date(dateValue).toLocaleString();
    } catch {
      return "";
    }
  };

  return (
    <div className={styles.userActionSectionCompact}>
      <h4 className={styles.sectionTitle}>User Action</h4>

      <div className={styles.userActionFieldsStacked}>
        {/* Status Dropdown */}
        <div className={styles.userActionFieldCompact}>
          <label htmlFor="userActionStatus">Status</label>
          <Select
            value={status}
            onChange={(selected: React.SetStateAction<StatusOption | null>) => setStatus(selected)}
            options={statusOptions}
            className="form-select custom-select"
            aria-label="User Action Status"
            closeMenuOnSelect={true}
            autosize={true}
            placeholder="Select status..."
            classNamePrefix="react-select"
            isDisabled={isSaving || isLoading}
            isClearable
          />
        </div>

        {/* Comment Input */}
        <div className={styles.userActionFieldCompact}>
          <textarea
            id="userComment"
            className="form-control move-left custom-input"
            placeholder="Add a comment..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            autoComplete="nope"
            disabled={isSaving}
            maxLength={1000}
          />
        </div>

        {/* Save Button Row */}
        <div className={styles.userActionBtnRow}>
          <CustomButton
            progress={isSaving}
            onclick={handleSave}
            content={isSaving ? "Saving..." : "Save"}
            disable={!hasChanges || isSaving}
          />

          {saveMessage && (
            <span
              className={
                saveMessage.type === "success"
                  ? styles.saveSuccess
                  : styles.saveError
              }
            >
              {saveMessage.text}
            </span>
          )}
        </div>

        {/* Last updated info */}
        {updatedBy && (
          <div className={styles.lastUpdatedInfoCompact}>
            Updated by {updatedBy}
            {updatedDate && ` on ${formatDate(updatedDate)}`}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserActionSection;